<template>
  <!-- Detailed View -->
  <div v-if="view === 'detailed'" :class="['rounded-xl px-4 py-2 md:p-6 text-white', gradientClass]">
    <div class="flex items-center justify-between mb-4">
      <div :class="['w-12 h-12 rounded-lg flex items-center justify-center', iconBgClass]">
        <span class="material-symbols-outlined text-white text-3xl">{{ mainIcon }}</span>
      </div>
      <span v-if="showTrend" class="material-symbols-outlined text-white text-2xl">{{ trendIcon }}</span>
    </div>
    <div class="flex md:flex-col items-center md:items-start gap-4 mb-2">
        <h3 class="text-3xl font-bold mb-1">{{ formattedCount }}</h3>
        <p :class="['text-sm', subtitleClass]">{{ subtitle }}</p>
    </div>
  </div>

  <!-- Simple View (Filter Button) -->
  <button
    v-else
    :class="[
      'relative flex items-center space-x-3 px-2 py-1 md:px-6 md:py-3 rounded-xl border-2 text-sm md:text-base transition-all overflow-hidden',
      //simpleViewClasses.bg,
      simpleViewClasses.text,
      simpleViewClasses.border,
      simpleViewClasses.hover,
      //{ 'ring-2 ring-offset-2': isActive }
      { [simpleViewClasses.bg]: isActive }
    ]"
    @click="handleClick"
  >
    <!-- Active indicator - filled top-right corner -->
      <!--
    <span
      v-if="isActive"
      :class="['absolute top-0 right-0 w-5 h-5', simpleViewClasses.bg]"
      style="clip-path: polygon(100% 0, 100% 100%, 0 0)"
    ></span>
    -->

    <span :class="['material-symbols-outlined text-xl', simpleViewClasses.iconColor]">{{ mainIcon }}</span>
    <span class="md:hidden font-semibold">{{ subtitleMobile || subtitle }}</span>
    <span class="hidden md:block font-semibold">{{ subtitle }}</span>
    <span :class="['px-2 py-0.5 rounded-full text-xs font-bold', badgeClasses]">
      {{ formattedCount }}
    </span>
  </button>
</template>

<script setup>
import { computed } from 'vue';

const props = defineProps({
  count: {
    type: Number,
    required: true
  },
  subtitle: {
    type: String,
    required: true
  },
  subtitleMobile: {
    type: String
  },
  variant: {
    type: String,
    required: true,
    validator: (value) => ['video', 'group', 'tutor', 'material'].includes(value)
  },
  view: {
    type: String,
    default: 'detailed',
    validator: (value) => ['detailed', 'simple'].includes(value)
  },
  showTrend: {
    type: Boolean,
    default: true
  },
  isActive: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['click']);

const variantConfig = {
  video: {
    gradient: 'bg-gradient-to-br from-blue-500 to-blue-600',
    subtitle: 'text-blue-100',
    mainIcon: 'play_circle',
    trendIcon: 'trending_up',
    simple: {
      bg: 'bg-blue-100',
      text: 'text-blue-700',
      border: 'border-blue-200',
      hover: 'hover:border-blue-300 hover:shadow-md',
      iconColor: 'text-blue-600',
      badge: {
        bg: 'bg-blue-200',
        text: 'text-blue-800'
      }
    }
  },
  group: {
    gradient: 'bg-gradient-to-br from-green-500 to-green-600',
    subtitle: 'text-green-100',
    mainIcon: 'groups',
    trendIcon: 'trending_up',
    simple: {
      bg: 'bg-green-100',
      text: 'text-green-700',
      border: 'border-green-200',
      hover: 'hover:border-green-300 hover:shadow-md',
      iconColor: 'text-green-600',
      badge: {
        bg: 'bg-green-200',
        text: 'text-green-800'
      }
    }
  },
  tutor: {
    gradient: 'bg-gradient-to-br from-purple-500 to-purple-600',
    subtitle: 'text-purple-100',
    mainIcon: 'person',
    trendIcon: 'trending_up',
    simple: {
      bg: 'bg-purple-100',
      text: 'text-purple-700',
      border: 'border-purple-200',
      hover: 'hover:border-purple-300 hover:shadow-md',
      iconColor: 'text-purple-600',
      badge: {
        bg: 'bg-purple-200',
        text: 'text-purple-800'
      }
    }
  },
  material: {
    gradient: 'bg-gradient-to-br from-orange-500 to-orange-600',
    subtitle: 'text-orange-100',
    mainIcon: 'description',
    trendIcon: 'trending_up',
    simple: {
      bg: 'bg-orange-100',
      text: 'text-orange-600',
      border: 'border-orange-200',
      hover: 'hover:border-orange-300 hover:shadow-md',
      iconColor: 'text-orange-600',
      badge: {
        bg: 'bg-orange-200',
        text: 'text-orange-700'
      }
    }
  }
};

const gradientClass = computed(() => variantConfig[props.variant].gradient);
const subtitleClass = computed(() => variantConfig[props.variant].subtitle);
const mainIcon = computed(() => variantConfig[props.variant].mainIcon);
const trendIcon = computed(() => variantConfig[props.variant].trendIcon);

const iconBgClass = computed(() => {
  const bgMap = {
    video: 'bg-blue-400',
    group: 'bg-green-400',
    tutor: 'bg-purple-400',
    material: 'bg-orange-400'
  };
  return bgMap[props.variant];
});

const activeCornerClass = computed(() => {
  const cornerMap = {
    video: 'bg-blue-500',
    group: 'bg-green-500',
    tutor: 'bg-purple-500',
    material: 'bg-orange-500'
  };
  return cornerMap[props.variant];
});

const simpleViewClasses = computed(() => variantConfig[props.variant].simple);
const badgeClasses = computed(() => {
  const badge = variantConfig[props.variant].simple.badge;
  return `${badge.bg} ${badge.text}`;
});

const formattedCount = computed(() => {
  return props.count.toLocaleString();
});

const handleClick = () => {
  emit('click');
};
</script>
